import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { authedClient, getStoredJwt, setStoredJwt, isApiError } from "../lib/api";
import type {
  StorefrontLoginResponse,
  StorefrontMetaResponse,
} from "../types/storefront-api";

interface AuthState {
  isAuthed: boolean;
  user: StorefrontMetaResponse["auth"]["user"];
  permissions: Array<Record<string, string>>;
  loading: boolean;
}

interface AuthApi extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthApi | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(() => ({
    isAuthed: Boolean(getStoredJwt()),
    user: null,
    permissions: [],
    loading: true,
  }));
  const queryClient = useQueryClient();

  const refresh = async () => {
    if (!getStoredJwt()) {
      setState({ isAuthed: false, user: null, permissions: [], loading: false });
      return;
    }
    try {
      const me = (await authedClient.get("/api/v1/storefront/me")) as
        | StorefrontMetaResponse["auth"]["user"]
        | null;
      setState({
        isAuthed: Boolean(me),
        user: me ?? null,
        permissions: [],
        loading: false,
      });
    } catch (e) {
      if (isApiError(e) && (e.status === 401 || e.status === 403)) {
        setStoredJwt(null);
      }
      setState({ isAuthed: false, user: null, permissions: [], loading: false });
    }
  };

  useEffect(() => {
    void refresh();
    // We willen 1x op mount checken; daarna alleen na expliciete login/logout.
     
  }, []);

  const login = async (email: string, password: string) => {
    // POST /storefront/auth/login is geen authed-endpoint (geen JWT vereist), maar we
    // gebruiken `authedClient` zodat de Bearer-header consistent kan worden gezet
    // bij subsequent calls. De backend negeert ontbrekende auth bij login.
    const real = (await authedClient.post("/api/v1/storefront/auth/login", {
      email,
      password,
    })) as StorefrontLoginResponse;
    setStoredJwt(real.access_token);
    setState({
      isAuthed: true,
      user: { id: real.user.id, email: real.user.email, name: real.user.name ?? "" },
      permissions: real.user.permissions ?? [],
      loading: false,
    });
    queryClient.invalidateQueries({ queryKey: ["storefront"] });
  };

  const logout = async () => {
    try {
      await authedClient.post("/api/v1/storefront/auth/logout", {});
    } catch {
      /* backend kan 401 geven; we wissen sowieso lokaal */
    }
    setStoredJwt(null);
    setState({ isAuthed: false, user: null, permissions: [], loading: false });
    queryClient.clear();
  };

  return (
    <AuthContext.Provider value={{ ...state, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthApi {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
